import { useCallback, useEffect, useMemo } from 'react';
import { useGroupedPromoModel } from './useGroupedPromoModel';
import {
  getPromotionItemsFromProps,
  mapPromotionItemsToProducts,
  shouldSkipPreselection,
} from '../utils/promo-grouped-utils';

/**
 * Custom hook to sync selected items model with promotionItemsFromProps.
 *
 * Flow:
 * 1. On drawer open: Pre-select items from promotionItemsFromProps so progress shows correctly
 * 2. During interaction: User selects/deselects items in drawer, progress updates via selectedItemsModel
 * 3. On "Add to Bundle": Consumer updates items prop, which becomes new promotionItemsFromProps
 *
 * @param {Object} params - Parameters object
 * @param {Map} params.allListProductsAndAnchor - Map of all products and anchor items
 * @param {Object} params.groupedItemsByAnchor - Grouped items organized by anchor (contains promotionItemsFromProps)
 * @param {Object} params.anchorItem - The anchor item object
 * @param {Function} params.setSelectedItemsModel - Function to update selected items model (drives progress UI)
 */
export const useSelectedItemsSync = ({
  allListProductsAndAnchor,
  groupedItemsByAnchor,
  anchorItem,
  setSelectedItemsModel,
}) => {
  // Get grouped promotion context if available
  const groupedContext = useGroupedPromoModel();
  const isGroupedContext = groupedContext?.isGroupedContext || false;
  const currentActiveGroup = groupedContext?.currentActiveGroup;

  // Anchor item that unlocked the promotion
  const anchorItemId = useMemo(() => anchorItem?.itemId, [anchorItem?.itemId]);

  // Extract promotionItemsFromProps - these are the items passed in by the consumer
  const promotionItemsFromProps = useMemo(() => {
    return getPromotionItemsFromProps(groupedItemsByAnchor, anchorItemId);
  }, [groupedItemsByAnchor, anchorItemId]);

  // Stable reference to setSelectedItemsModel
  const stableSetSelectedItemsModel = useCallback(setSelectedItemsModel, [setSelectedItemsModel]);

  // Pre-select items from promotionItemsFromProps when data loads, changes, or accordion opens
  // This ensures progress shows correctly when drawer opens
  useEffect(() => {
    // Skip if no data or not enough product data loaded
    if (
      !promotionItemsFromProps?.length
      || !allListProductsAndAnchor
      || allListProductsAndAnchor.size < promotionItemsFromProps.length
    ) {
      return;
    }

    // In grouped context, only pre-select when this group is active
    if (shouldSkipPreselection({ isGroupedContext, currentActiveGroup, anchorItemId })) {
      return;
    }

    // Map promotionItemsFromProps itemIds to full product data
    const preSelectedItems = mapPromotionItemsToProducts({
      promotionItemsFromProps,
      allListProductsAndAnchor,
    });

    // Only update if we have items to pre-select
    if (preSelectedItems.length > 0) {
      stableSetSelectedItemsModel(preSelectedItems);
    }
  }, [
    promotionItemsFromProps,
    allListProductsAndAnchor,
    anchorItemId,
    stableSetSelectedItemsModel,
    isGroupedContext,
    currentActiveGroup,
  ]);
};
